import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import PageBanner from '../../components/ui/PageBanner';
import SectionHeading from '../../components/ui/SectionHeading';
import Button from '../../components/ui/Button';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import nosCycles from '../../assets/images/NosCycles.webp';
import maternelle1 from '../../assets/images/slideMaternelle/Maternelle1.webp';
import maternelle2 from '../../assets/images/slideMaternelle/Maternelle2.webp';
import maternelle3 from '../../assets/images/slideMaternelle/Maternelle3.webp';
import maternelle4 from '../../assets/images/slideMaternelle/Maternelle4.webp';
import primaire from '../../assets/images/Primaire1.webp';
import college from '../../assets/images/College1.webp';
import lycee from '../../assets/images/Lycee.webp';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination, Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';

interface Program {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  images?: string[];
  image?: string;
  features: string[];
}

const programs: Program[] = [
  {
    id: 'maternelle',
    title: 'Maternelle',
    subtitle: 'Âges 3-5 ans',
    description: "Notre programme préscolaire offre un environnement chaleureux et stimulant où les enfants peuvent explorer, découvrir et développer leurs capacités. Nous nous concentrons sur le développement global de l'enfant, combinant apprentissage et jeu.",
    images: [maternelle1, maternelle2, maternelle3, maternelle4],
    features: [
      'Développement des compétences linguistiques en arabe et français',
      'Activités de pré-lecture et pré-écriture',
      'Découverte des concepts mathématiques et scientifiques de base',
      'Activités artistiques et musicales',
      'Développement socio-émotionnel',
      'Activités motrices et sportives adaptées',
    ],
  },
  {
    id: 'primaire',
    title: 'Primaire',
    subtitle: 'Âges 6-11 ans',
    description: "Notre cycle primaire pose les bases solides d'une éducation de qualité. Nous offrons un programme équilibré qui développe les compétences fondamentales tout en encourageant la créativité, l'esprit critique et la confiance en soi.",
    image: primaire,
    features: [
      'Programme aligné sur le curriculum national marocain',
      'Enseignement trilingue: arabe, français et initiation à l\'anglais',
      'Développement des compétences en lecture, écriture et mathématiques',
      'Initiation aux sciences et à la technologie',
      'Initiation à l\'intelligence artificielle dans un cadre sécurisé (Gemini, Canva Éducation, Google for Education)',
      'Éducation artistique et musicale',
      'Éducation physique et sportive',
    ],
  },
  {
    id: 'college',
    title: 'Collège',
    subtitle: 'Âges 12-14 ans',
    description: "Au collège, nous préparons les élèves à développer leur autonomie et leur esprit critique. Notre programme offre un équilibre entre apprentissage académique rigoureux et développement des compétences essentielles pour réussir.",
    image: college,
    features: [
      'Enseignement approfondi en arabe, français et anglais',
      'Mathématiques et sciences (physique, chimie, sciences de la vie et de la terre)',
      'Histoire-géographie et éducation civique',
      'Informatique et technologie',
      'Arts plastiques et éducation musicale',
      'Éducation physique et sportive',
    ],
  },
  {
    id: 'lycee',
    title: 'Lycée',
    subtitle: 'Âges 15-17 ans',
    description: "Notre lycée prépare les élèves à l'excellence académique et aux défis de l'enseignement supérieur et du monde professionnel. Nous offrons différentes filières adaptées aux intérêts et aux projets d'avenir de chaque élève.",
    image: lycee,
    features: [
      'Options d\'orientation: Sciences Mathématiques, Sciences Physiques, Sciences de la Vie et de la Terre, Lettres et Sciences Humaines',
      'Préparation aux examens nationaux',
      'Orientation académique et professionnelle',
      'Préparation aux études supérieures',
      'Activités parascolaires enrichissantes',
      'Projets éducatifs innovants',
    ],
  },
];

const ProgramsPage = () => {
  const location = useLocation();
  const [activeSection, setActiveSection] = useState<string | null>(null);
  
  useEffect(() => {
    // Check if there's a hash in the URL
    if (location.hash) {
      const id = location.hash.replace('#', '');
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setActiveSection(id);
      }
    }
  }, [location]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <PageBanner
        title="Nos Cycles d'Enseignement"
        subtitle="De la maternelle au lycée"
        imageSrc={nosCycles}
      />

      {/* Introduction Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <SectionHeading
            title="Notre approche éducative"
            subtitle="Excellence et épanouissement"
            centered={true}
          />
          <div className="max-w-3xl mx-auto mt-8 text-center">
            <p className="text-lg text-gray-700 mb-8">
              Au Groupe Scolaire Sanaa, nous offrons un parcours éducatif complet et cohérent de la maternelle au lycée. Notre approche pédagogique est centrée sur l'élève, favorisant l'acquisition des savoirs, le développement des compétences et l'épanouissement personnel.
            </p>
            <p className="text-lg text-gray-700 mb-8">
              Nous croyons en l'importance d'une éducation multilingue et multiculturelle, préparant nos élèves à s'intégrer dans un monde globalisé tout en restant attachés à leurs racines et leur identité culturelle.
            </p>
            <Button to="/contact" variant="primary">
              Demander plus d'informations
            </Button>
          </div>
        </div>
      </section>

      {/* Navigation Pills */}
      <section className="py-8 bg-gray-50 sticky top-0 z-30 shadow-sm">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-4">
            {programs.map((program) => (
              <Button
                key={program.id}
                to={`#${program.id}`}
                variant={activeSection === program.id ? "primary" : "ghost"}
                onClick={() => setActiveSection(program.id)}
              >
                {program.title}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Programs Sections */}
      {programs.map((program, index) => (
        <section 
          key={program.id}
          id={program.id}
          className={`py-16 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
        >
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <motion.div
                className={`${index % 2 === 1 ? 'md:order-2' : ''}`}
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
              >
                <SectionHeading
                  title={program.title}
                  subtitle={program.subtitle}
                />
                <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                  {program.description}
                </p>
                <h3 className="text-xl font-semibold mb-4 text-gray-900">Ce que nous offrons :</h3>
                <ul className="space-y-3 mb-8">
                  {program.features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <CheckCircleIcon className="h-6 w-6 text-primary mt-0.5 mr-3 flex-shrink-0" />
                      <p className="text-gray-700">{feature}</p>
                    </li>
                  ))}
                </ul>
                <Button to="/contact" variant="primary">
                  En savoir plus
                </Button>
              </motion.div>
              <motion.div
                className={`${index % 2 === 1 ? 'md:order-1' : ''}`}
                initial={{ opacity: 0, x: index % 2 === 0 ? 30 : -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
              >
                <div className="relative w-full h-[600px]">
                  <div className={`absolute -top-4 ${index % 2 === 0 ? '-right-4' : '-left-4'} 
                    w-32 h-32 bg-primary rounded-tr-3xl z-0`}></div>
                  
                  {program.id === 'maternelle' ? (
                    <Swiper
                      modules={[Autoplay, Pagination, Navigation]}
                      spaceBetween={30}
                      centeredSlides={true}
                      autoplay={{
                        delay: 3000,
                        disableOnInteraction: false,
                      }}
                      pagination={{
                        clickable: true,
                      }}
                      navigation={true}
                      className="rounded-lg shadow-elegant w-full h-[600px] relative z-10"
                    >
                      {program.images?.map((image, idx) => (
                        <SwiperSlide key={idx}>
                          <img 
                            src={image}
                            alt={`${program.title} - Image ${idx + 1}`}
                            className="w-full h-[600px] object-cover"
                          />
                        </SwiperSlide>
                      ))}
                    </Swiper>
                  ) : (
                    <img 
                      src={program.image}
                      alt={program.title} 
                      className="rounded-lg shadow-elegant w-full h-[600px] object-cover relative z-10"
                    />
                  )}
                  
                  <div className={`absolute -bottom-4 ${index % 2 === 0 ? '-left-4' : '-right-4'} 
                    w-32 h-32 bg-secondary rounded-bl-3xl z-0`}></div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      ))}

      {/* CTA Section */}
      <section className="py-16 bg-primary">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Prêt à offrir une éducation d'excellence à votre enfant ?</h2>
          <p className="text-xl text-white/90 mb-8 max-w-3xl mx-auto">
            Contactez-nous dès aujourd'hui pour en savoir plus sur nos programmes et pour planifier une visite de notre établissement.
          </p>
          <Button to="/contact" variant="outline-white" size="large">
            Nous contacter
          </Button>
        </div>
      </section>
    </motion.div>
  );
};

export default ProgramsPage;
